import React, { useEffect, useState } from 'react';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import TabsWithDots, { ITab } from 'components/TabsWithDots/TabsWithDots';
import emailNotValid from 'utils/objectsValidation/emailNotValid';
import { UsersService } from 'services/users/users';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import invalidateCache, { InvalidateArray } from 'utils/api/invalidateCache';
import { useDispatch } from 'react-redux';
import { setToast } from 'redux/modules/toast/actions';
import { throwError } from 'rxjs';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/views/users/addUserDialogStyle';
import { makeStyles } from '@material-ui/core';
import { finalize } from 'rxjs/operators';
import { MAX_ADMIN_COUNT } from 'utils/const/users';
import Confirmation from 'views/Users/common/AddUserDialog/subcomponents/Confirmation';
import UserTypeTab from 'views/Users/common/AddUserDialog/subcomponents/UserTypeTab';
import SummaryTab from './subcomponents/SummaryTab';
import OrganizationAndDepartmentTab from './subcomponents/OrganizationAndDepartmentTab';
import PermissionsTab from './subcomponents/DepartmentPermissionsTab';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import moment from 'moment';
import { PHONE_NUMBER_LENGTH } from 'utils/const/numbers';
import BillingAccountsTab from 'views/Users/common/AddUserDialog/subcomponents/BillingAccountsTab';
import { UserType } from 'views/Users/UsersList';

// @ts-ignore
const useStyles = makeStyles(styles);
const DEFAULT_USER_TYPE = 'ENDUSER';

interface IAddUserDialogProps {
	isDialogOpen: boolean;
	setIsDialogOpen: (isOpen: boolean) => void;
	userType?: types.users.userType;
	dialogHeader: string;
	accounts: SingleCorpAccount[];
	account?: SingleCorpAccount;
	departments: SingleDepartment[];
	userAddedSuccessfully: () => void;
	toastSuccessMessage: string;
}

const ALL_TABS_LENGTH = 4;

const AddUserDialog = ({
	isDialogOpen,
	setIsDialogOpen,
	dialogHeader,
	userType,
	accounts,
	account,
	departments,
	userAddedSuccessfully,
}: IAddUserDialogProps) => {
	const [activeTab, setActiveTab] = useState<number>(0);
	const [userName, setUserName] = useState<string>('');
	const [emailAddress, setEmailAddress] = useState<string>('');
	const [contactNumber, setContactNumber] = useState<string>('');
	const [showValidation, setShowValidation] = useState<boolean>(false);
	const [assignedAccount, setAssignedAccount] = useState<types.users.IAssignedAccount | null>(null);
	const [filteredDepartments, setFilteredDepartments] = useState<SingleDepartment[]>([]);
	const [selectedDepartment, setSelectedDepartment] = useState<SingleDepartment | null>(null);
	const [selectedUserType, setSelectedUserType] = useState<types.users.userType>(DEFAULT_USER_TYPE);
	const [selectedBillingIds, setSelectedBillingIds] = useState<string[]>([]);
	const [expirationDate, setExpirationDate] = useState<moment.Moment | null>(null);
	const [billingAccountExpirationDate, setBillingAccountExpirationDate] = useState<moment.Moment | null>(null);
	const [unlimitedExpirationDate, setUnlimitedExpirationDate] = useState<boolean>(true);
	const [billingAccountUnlimitedExpirationDate, setBillingAccountUnlimitedExpirationDate] = useState<boolean>(true);
	const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
	const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
	const [userExistsError, setUserExistsError] = useState<boolean>(false);
	const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
	const [userId, setUserId] = useState<string>('');

	const classes = useStyles();
	const dispatch = useDispatch();
	const accountTabHidden = (accounts.length < 2 || account) && filteredDepartments.length <= 0;
	const permissionsTabHidden = selectedUserType === UserType.ENDUSER;
	const billingAccountTabHidden = selectedUserType !== UserType.MANAGER;

	const numberOfTabs =
		ALL_TABS_LENGTH - [accountTabHidden, permissionsTabHidden, billingAccountTabHidden].filter(Boolean).length;

	const lastTab = activeTab === numberOfTabs;

	useEffect(() => {
		if (account) {
			setAssignedAccount({
				id: account.accountId,
				name: account.accountName,
				disabled: Number(account.adminsCount) >= MAX_ADMIN_COUNT,
			});
			if (Number(account.adminsCount) < MAX_ADMIN_COUNT) {
				setSelectedAccounts([account.accountId]);
			}
		} else if (accounts.length === 1) {
			setAssignedAccount({
				id: accounts[0].accountId,
				name: accounts[0].accountName,
				disabled: Number(accounts[0].adminsCount) >= MAX_ADMIN_COUNT,
			});
			if (Number(accounts[0].adminsCount) < MAX_ADMIN_COUNT) {
				setSelectedAccounts([accounts[0].accountId]);
			}
		} else {
			setAssignedAccount(null);
		}
	}, [account, accounts]);

	useEffect(() => {
		if (userType) {
			setSelectedUserType(userType);
		}
	}, [userType]);

	useEffect(() => {
		setSelectedDepartment(null);
	}, [assignedAccount]);

	useEffect(() => {
		if (assignedAccount) {
			setFilteredDepartments(
				departments.filter((department) => String(department.accountId) === assignedAccount.id)
			);
		} else {
			setFilteredDepartments([]);
		}
	}, [assignedAccount, departments]);

	const clearDialog = () => {
		setActiveTab(0);
		setEmailAddress('');
		setUserName('');
		setShowValidation(false);
		setShowConfirmation(false);
		setUserExistsError(false);
		setContactNumber('');
		if (account) {
			setAssignedAccount({
				id: account.accountId,
				name: account.accountName,
			});
		} else if (accounts.length === 1) {
			setAssignedAccount({
				id: accounts[0].accountId,
				name: accounts[0].accountName,
			});
		} else {
			setAssignedAccount(null);
		}
		setSelectedDepartment(null);
		setExpirationDate(null);
		setUnlimitedExpirationDate(true);
		setSelectedAccounts([]);
		setSelectedDepartments([]);
		if (userType) {
			setSelectedUserType(userType);
		} else {
			setSelectedUserType(DEFAULT_USER_TYPE);
		}
		setSelectedBillingIds([]);
		setBillingAccountExpirationDate(null);
		setBillingAccountUnlimitedExpirationDate(true);
	};

	const hideDialog = () => {
		clearDialog();
		setIsDialogOpen(false);
		setShowConfirmation(false);
	};

	const nextStep = () => {
		if (activeTab === 0) {
			if (
				(selectedUserType === 'ENDUSER' && emailAddress.length > 0 && emailNotValid(emailAddress)) ||
				(selectedUserType !== 'ENDUSER' && emailNotValid(emailAddress)) ||
				(selectedUserType !== 'ENDUSER' && contactNumber.length !== PHONE_NUMBER_LENGTH)
			) {
				setShowValidation(true);
				return;
			}
		}
		setShowValidation(false);
		if (lastTab) {
			addUser();
		} else {
			setActiveTab((value) => value + 1);
		}
	};

	const nextStepDisabled = (() => {
		if (activeTab === 0) {
			return (
				userName.length <= 0 ||
				(selectedUserType !== 'ENDUSER' && emailAddress.length <= 0) ||
				(selectedUserType !== 'ENDUSER' && contactNumber.length <= 0)
			);
		}
		if (activeTab === 1 && !accountTabHidden) {
			return !assignedAccount;
		}
		if (
			(activeTab === 2 && !accountTabHidden && !permissionsTabHidden) ||
			(activeTab === 1 && accountTabHidden && !permissionsTabHidden)
		) {
			return !expirationDate && !unlimitedExpirationDate;
		}
		if (
			(activeTab === 3 && !accountTabHidden && !permissionsTabHidden) ||
			(activeTab === 2 && accountTabHidden && !permissionsTabHidden)
		) {
			return !billingAccountExpirationDate && !billingAccountUnlimitedExpirationDate;
		}
		return false;
	})();

	const addUser = () => {
		dispatch(showLoadingIndicator());
		const cacheToInvalidate: InvalidateArray = [
			'getCorpAccountDetails',
			'getDepartmentsForCurrentUser',
			'getDepartmentDetails',
			'getCorpUsersForAccounts',
		];
		const data = {
			username: userName,
			userType: selectedUserType,
			accountId: assignedAccount?.id || '',
			email: '',
		};
		if (selectedDepartment) {
			data['departmentId'] = selectedDepartment.deptId;
		}
		if (contactNumber) {
			data['contactPhone1'] = contactNumber;
		}
		if (!unlimitedExpirationDate && expirationDate && selectedUserType !== 'ENDUSER') {
			data['permissionExpiration'] = expirationDate.format('YYYY-MM-DD');
		}
		if (!billingAccountUnlimitedExpirationDate && billingAccountExpirationDate && selectedUserType === 'MANAGER') {
			data['billingPermissionExpiration'] = billingAccountExpirationDate.format('YYYY-MM-DD');
		}
		if (selectedUserType === 'ADMIN') {
			data['accountsIdsToManage'] = selectedAccounts;
			cacheToInvalidate.push('getCorpAccountAdmins');
		}
		if (selectedUserType !== 'ENDUSER' || emailAddress) {
			data['email'] = emailAddress;
		}
		if (selectedUserType === 'MANAGER') {
			data['departamentsIdsToManage'] = selectedDepartments;
			cacheToInvalidate.push('getCorpAccountManagers');
		}
		if (selectedUserType === 'MANAGER') {
			data['billingAccountIdsToManage'] = selectedBillingIds;
			cacheToInvalidate.push('getCorpAccountManagers');
		}
		UsersService.createCorpUser(data)
			.pipe(finalize(() => dispatch(hideLoadingIndicator())))
			.subscribe(
				({ response }) => {
					const corpUserId = response?.createCorpUserResponse?.arguments?.corpUserId;
					if (corpUserId) {
						setIsDialogOpen(false);
						invalidateCache(cacheToInvalidate, true);
						setShowConfirmation(true);
						setUserId(corpUserId);
					} else {
						dispatch(
							setToast({
								error: true,
								label: translate('error.generic-title'),
								actionType: translate('error'),
							})
						);
					}
				},
				(error) => {
					if (error?.response?.code === 'USER_ALREADY_EXISTS') {
						setActiveTab(0);
						setUserExistsError(true);
					} else {
						throwError(error);
					}
				}
			);
	};

	if (showConfirmation) {
		return (
			<Confirmation
				userName={userName}
				userType={selectedUserType}
				userId={userId}
				hideDialog={() => {
					setShowConfirmation(false);
					userAddedSuccessfully && userAddedSuccessfully();
					clearDialog();
				}}
			/>
		);
	}

	return (
		<CustomDialog
			isOpen={isDialogOpen}
			hideDialog={hideDialog}
			header={dialogHeader ? dialogHeader : translate('add.user')}
			rightSideButtons={[
				{
					text: activeTab === 0 ? translate('cancel') : translate('back'),
					onClick: () => {
						if (activeTab === 0) {
							hideDialog();
						} else {
							setActiveTab((value) => value - 1);
						}
					},
					color: 'tertiary-light',
				},
				{
					text: lastTab ? translate('add') : translate('next'),
					onClick: nextStep,
					id: 'next-step-button',
					color: 'primary-light',
					disabled: nextStepDisabled,
				},
			]}
			dialogBody={
				<div className={classes.dialogBodyWrapper}>
					<TabsWithDots
						tabs={
							[
								{
									name: translate('user.type'),
									content: (
										<UserTypeTab
											showValidation={showValidation}
											accounts={accounts}
											userType={userType}
											selectedUserType={selectedUserType}
											setSelectedUserType={setSelectedUserType}
											emailAddress={emailAddress}
											setEmailAddress={setEmailAddress}
											userName={userName}
											setUserName={setUserName}
											userExistsError={userExistsError}
											setUserExistsError={setUserExistsError}
											contactNumber={contactNumber}
											setContactNumber={setContactNumber}
										/>
									),
								},
								!accountTabHidden && {
									name: translate('organization.and.department'),
									content: (
										<OrganizationAndDepartmentTab
											accounts={accounts}
											account={account}
											selectedUserType={selectedUserType}
											assignedAccount={assignedAccount}
											setAssignedAccount={setAssignedAccount}
											setSelectedAccounts={setSelectedAccounts}
											filteredDepartments={filteredDepartments}
											selectedDepartment={selectedDepartment}
											setSelectedDepartment={setSelectedDepartment}
										/>
									),
								},
								!permissionsTabHidden && {
									name: translate('department.permissions'),
									content: (
										<PermissionsTab
											departments={departments}
											accounts={accounts}
											selectedUserType={selectedUserType}
											setUnlimitedExpirationDate={setUnlimitedExpirationDate}
											unlimitedExpirationDate={unlimitedExpirationDate}
											setExpirationDate={setExpirationDate}
											expirationDate={expirationDate}
											selectedAccounts={selectedAccounts}
											setSelectedAccounts={setSelectedAccounts}
											assignedAccount={assignedAccount}
											selectedDepartments={selectedDepartments}
											setSelectedDepartments={setSelectedDepartments}
										/>
									),
								},
								!billingAccountTabHidden && {
									name: translate('billing.account.permissions'),
									content: (
										<BillingAccountsTab
											selectedBillingIds={selectedBillingIds}
											setSelectedBillingIds={setSelectedBillingIds}
											billingAccountExpirationDate={billingAccountExpirationDate}
											setBillingAccountExpirationDate={setBillingAccountExpirationDate}
											setBillingAccountUnlimitedExpirationDate={
												setBillingAccountUnlimitedExpirationDate
											}
											billingAccountUnlimitedExpirationDate={
												billingAccountUnlimitedExpirationDate
											}
										/>
									),
								},
								{
									name: translate('summary'),
									content: (
										<SummaryTab
											userName={userName}
											emailAddress={emailAddress}
											selectedUserType={selectedUserType}
											accounts={accounts}
											selectedDepartments={selectedDepartments}
											departments={departments}
											permissionsTabHidden={permissionsTabHidden}
											billingAccountTabHidden={billingAccountTabHidden}
											selectedDepartment={selectedDepartment}
											selectedAccounts={selectedAccounts}
											selectedBillingIds={selectedBillingIds}
											expirationDate={expirationDate}
											unlimitedExpirationDate={unlimitedExpirationDate}
											billingAccountExpirationDate={billingAccountExpirationDate}
											billingAccountUnlimitedExpirationDate={
												billingAccountUnlimitedExpirationDate
											}
											assignedAccount={assignedAccount}
											contactNumber={contactNumber}
										/>
									),
								},
							].filter(Boolean) as ITab[]
						}
						activeTab={activeTab}
						setActiveTab={(value) => setActiveTab(value)}
					/>
				</div>
			}
		/>
	);
};

export default AddUserDialog;
